import { useState, type CSSProperties, type ReactNode } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Grid,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  CopyButton,
  Tooltip,
  UnstyledButton,
  rem,
} from '@mantine/core';
import {
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconInfoCircle,
  IconLogin2,
  IconShieldCheck,
  IconAppWindow,
  IconCode,
  IconMail,
  IconListCheck,
  IconAlertTriangle,
  IconHelpCircle,
  IconBook2,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { pageWrapper } from '@/Styles';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { useStore } from '@/store/Store';
import { isCurrentUserMasterAdmin } from '@/utils/masterAdminUtils';
import { clientRoutes } from '@/ApiPaths';
import {
  ROBOTOP_POPUP_LOGIN_MSG,
  ROBOTOP_UNITY_SESSION_MSG,
} from '@/services/unitySessionHandoff';
import classes from './PartnerIntegrationGuide.module.css';

/** Always document production endpoints — partners integrate against live Robotop. */
const PRODUCTION_ORIGIN = 'https://portfolio.robotop.de';
const PRODUCTION_LOGIN = `${PRODUCTION_ORIGIN}/login`;
const PRODUCTION_UNITY_IFRAME = `${PRODUCTION_ORIGIN}/api/ecom/app`;
const PRODUCTION_UNITY_ORIGIN = PRODUCTION_ORIGIN;

const TOC = [
  { id: 'overview', key: 'tocOverview' },
  { id: 'start', key: 'tocStart' },
  { id: 'how', key: 'tocHow' },
  { id: 'steps', key: 'tocSteps' },
  { id: 'snippet', key: 'tocSnippet' },
  { id: 'checklist', key: 'tocChecklist' },
  { id: 'faq', key: 'tocFaq' },
  { id: 'security', key: 'tocSecurity' },
] as const;

function CodeBlock({ code, label }: { code: string; label?: string }) {
  const { t } = useTranslation();
  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      style={{
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        borderColor: 'rgba(15, 23, 42, 0.08)',
      }}
    >
      {label ? (
        <Text size="xs" c="dimmed" mb={8} fw={700} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
          {label}
        </Text>
      ) : null}
      <Group justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
        <Code
          block
          style={{
            flex: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: 'transparent',
            fontSize: rem(13),
            lineHeight: 1.55,
          }}
        >
          {code}
        </Code>
        <CopyButton value={code} timeout={1500}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('partnerGuide.copied') : t('partnerGuide.copy')} withArrow>
              <UnstyledButton
                onClick={copy}
                aria-label={t('partnerGuide.copyCode')}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  border: '1px solid rgba(15, 23, 42, 0.1)',
                  background: '#fff',
                }}
              >
                {copied ? <IconCheck size={16} color="teal" /> : <IconCopy size={16} />}
              </UnstyledButton>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
    </Paper>
  );
}

function SectionCard({
  step,
  icon,
  title,
  children,
  id,
}: {
  step?: number;
  icon: ReactNode;
  title: string;
  children: ReactNode;
  id?: string;
}) {
  const { t } = useTranslation();
  return (
    <Paper
      id={id}
      withBorder
      p={{ base: 'md', sm: 'xl' }}
      radius="lg"
      style={{ borderColor: 'rgba(15, 23, 42, 0.08)' }}
    >
      <Group gap="md" mb="md" align="flex-start" wrap="nowrap">
        <ThemeIcon
          color={DEFAULT_QR_CODE_COLOR}
          variant="light"
          size={44}
          radius="md"
          style={{ flexShrink: 0 }}
        >
          {icon}
        </ThemeIcon>
        <div>
          {step != null ? (
            <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ letterSpacing: '0.06em' }}>
              {t('partnerGuide.stepLabel', { n: step })}
            </Text>
          ) : null}
          <Title order={2} fz={{ base: '1.25rem', sm: '1.4rem' }}>
            {title}
          </Title>
        </div>
      </Group>
      {children}
    </Paper>
  );
}

function FlowStep({ label, sub }: { label: string; sub: string }) {
  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      style={{
        height: '100%',
        background: '#fff',
        borderColor: 'rgba(15, 23, 42, 0.08)',
      }}
    >
      <Text fw={700} size="sm" mb={4}>
        {label}
      </Text>
      <Text size="xs" c="dimmed" lh={1.5}>
        {sub}
      </Text>
    </Paper>
  );
}

function PartnerPageFigure() {
  const { t } = useTranslation();
  return (
    <div className={classes.figure}>
      <div className={classes.browserChrome}>
        <span className={classes.dot} />
        <span className={classes.dot} />
        <span className={classes.dot} />
        <div className={classes.address}>{t('partnerGuide.mockYourSite')}</div>
      </div>
      <div className={classes.browserBody}>
        <div className={classes.partnerBar}>
          <span className={classes.partnerBrand}>{t('partnerGuide.mockYourBrand')}</span>
          <span className={classes.loginBtn}>{t('partnerGuide.mockLoginBtn')}</span>
        </div>
        <div className={classes.unityStage}>{t('partnerGuide.mockUnityLabel')}</div>
      </div>
      <p className={classes.figureCaption}>{t('partnerGuide.figurePartnerCaption')}</p>
    </div>
  );
}

function LoginPopupFigure() {
  const { t } = useTranslation();
  return (
    <div className={classes.figure}>
      <div className={classes.browserChrome}>
        <span className={classes.dot} />
        <span className={classes.dot} />
        <span className={classes.dot} />
        <div className={classes.address}>{t('partnerGuide.mockYourSite')}</div>
      </div>
      <div className={classes.browserBody} style={{ paddingTop: '1.25rem', paddingBottom: '1.4rem' }}>
        <div className={classes.popupMock}>
          <div className={classes.popupHeader}>{t('partnerGuide.mockPopupTitle')}</div>
          <div className={classes.popupBody}>
            <div className={classes.fakeField} />
            <div className={classes.fakeField} />
            <span className={classes.loginBtn} style={{ display: 'inline-block' }}>
              {t('partnerGuide.mockLoginBtn')}
            </span>
          </div>
        </div>
      </div>
      <p className={classes.figureCaption}>{t('partnerGuide.figurePopupCaption')}</p>
    </div>
  );
}

/**
 * Partner embed guide — navbar + page limited to Master Admins (same gate as Portfolio Admin).
 * Snippets always use production URLs (portfolio.robotop.de).
 */
export function PartnerIntegrationGuide() {
  const { t } = useTranslation();
  const userProfile = useStore((state) => state.userProfile);
  const [showFullSnippet, setShowFullSnippet] = useState(true);

  if (!isCurrentUserMasterAdmin(userProfile)) {
    return <Navigate to={clientRoutes.home} replace />;
  }

  const iframeSnippet = `<iframe
  id="robotop-unity"
  src="${PRODUCTION_UNITY_IFRAME}"
  title="Robotop Unity"
  width="100%"
  height="720"
  style="border:0;display:block;max-width:100%"
  allow="fullscreen"
  referrerpolicy="strict-origin-when-cross-origin"
></iframe>`;

  const popupSnippet = `const openerOrigin = window.location.origin; // e.g. https://www.your-site.com

const loginUrl = new URL('${PRODUCTION_LOGIN}');
loginUrl.searchParams.set('popup', '1');
loginUrl.searchParams.set('opener_origin', openerOrigin);

const width = 480;
const height = 720;
const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);

window.open(
  loginUrl.toString(),
  'robotop_login',
  \`popup=yes,width=\${width},height=\${height},left=\${left},top=\${top}\`
);`;

  const messageSnippet = `const UNITY_IFRAME_ORIGIN = '${PRODUCTION_UNITY_ORIGIN}';
const ROBOTOP_LOGIN_ORIGIN = '${PRODUCTION_ORIGIN}';
let lastTokens = null;

function postTokensToUnity(tokens) {
  lastTokens = tokens;
  const iframe = document.getElementById('robotop-unity');
  if (!iframe?.contentWindow || !tokens?.['st-access-token']) return;

  iframe.contentWindow.postMessage(
    {
      type: '${ROBOTOP_UNITY_SESSION_MSG}',
      accessToken: tokens['st-access-token'],
      frontToken: tokens['front-token'],
      tokens,
    },
    UNITY_IFRAME_ORIGIN // never use "*" for tokens
  );
}

window.addEventListener('message', (event) => {
  // 1) Tokens from the Robotop login popup
  if (
    event.origin === ROBOTOP_LOGIN_ORIGIN &&
    event.data?.type === '${ROBOTOP_POPUP_LOGIN_MSG}' &&
    event.data.status === 'ok'
  ) {
    postTokensToUnity(event.data.tokens);
    return;
  }

  // 2) Unity finished loading and asks for tokens again
  if (
    event.origin === UNITY_IFRAME_ORIGIN &&
    event.data?.type === 'robotop-unity-session-ready' &&
    lastTokens
  ) {
    postTokensToUnity(lastTokens);
  }
});`;

  const fullSnippet = `<!-- Robotop partner embed: Unity iframe + Login with Robotop -->
<iframe
  id="robotop-unity"
  src="${PRODUCTION_UNITY_IFRAME}"
  title="Robotop Unity"
  width="100%"
  height="720"
  style="border:0;display:block;max-width:100%"
  allow="fullscreen"
></iframe>

<button type="button" id="robotop-login">Login with Robotop</button>

<script>
(function () {
  const OPENER_ORIGIN = window.location.origin;
  const LOGIN_URL = '${PRODUCTION_LOGIN}';
  const UNITY_ORIGIN = '${PRODUCTION_UNITY_ORIGIN}';
  const ROBOTOP_ORIGIN = '${PRODUCTION_ORIGIN}';
  let lastTokens = null;

  function postTokensToUnity(tokens) {
    lastTokens = tokens;
    const iframe = document.getElementById('robotop-unity');
    if (!iframe?.contentWindow || !tokens?.['st-access-token']) return;
    iframe.contentWindow.postMessage(
      {
        type: '${ROBOTOP_UNITY_SESSION_MSG}',
        accessToken: tokens['st-access-token'],
        frontToken: tokens['front-token'],
        tokens,
      },
      UNITY_ORIGIN
    );
  }

  document.getElementById('robotop-login')?.addEventListener('click', function () {
    const loginUrl = new URL(LOGIN_URL);
    loginUrl.searchParams.set('popup', '1');
    loginUrl.searchParams.set('opener_origin', OPENER_ORIGIN);
    var w = 480, h = 720;
    var left = Math.max(0, window.screenX + (window.outerWidth - w) / 2);
    var top = Math.max(0, window.screenY + (window.outerHeight - h) / 2);
    window.open(
      loginUrl.toString(),
      'robotop_login',
      'popup=yes,width=' + w + ',height=' + h + ',left=' + left + ',top=' + top
    );
  });

  window.addEventListener('message', function (event) {
    if (
      event.origin === ROBOTOP_ORIGIN &&
      event.data &&
      event.data.type === '${ROBOTOP_POPUP_LOGIN_MSG}' &&
      event.data.status === 'ok'
    ) {
      postTokensToUnity(event.data.tokens);
      return;
    }
    if (
      event.origin === UNITY_ORIGIN &&
      event.data &&
      event.data.type === 'robotop-unity-session-ready' &&
      lastTokens
    ) {
      postTokensToUnity(lastTokens);
    }
  });
})();
</script>`;

  const accentStyle = { '--robotop-accent': DEFAULT_QR_CODE_COLOR } as CSSProperties;

  return (
    <Box className={classes.page} style={{ ...pageWrapper, ...accentStyle }}>
      <div className={classes.layout}>
        <nav className={classes.toc} aria-label={t('partnerGuide.toc')}>
          <div className={classes.tocTitle}>{t('partnerGuide.toc')}</div>
          {TOC.map((item) => (
            <a key={item.id} className={classes.tocLink} href={`#${item.id}`}>
              {t(`partnerGuide.${item.key}`)}
            </a>
          ))}
        </nav>

        <Stack gap="xl">
          <Box className={classes.hero}>
            <Badge color="gray" variant="light" mb="sm" leftSection={<IconBook2 size={12} />}>
              {t('partnerGuide.badge')}
            </Badge>
            <Title order={1} fz={{ base: 28, sm: 36 }} fw={800} maw={780} lh={1.15}>
              {t('partnerGuide.title')}
            </Title>
            <Text c="dimmed" mt="md" maw={720} lh={1.65} size="md">
              {t('partnerGuide.intro')}
            </Text>
            <div className={classes.audienceRow}>
              <span className={classes.audienceChip}>{t('partnerGuide.audienceEditors')}</span>
              <span className={classes.audienceChip}>{t('partnerGuide.audienceDevs')}</span>
            </div>
            <Group mt="xl" gap="sm">
              <Button
                component="a"
                href={PRODUCTION_ORIGIN}
                target="_blank"
                rel="noreferrer"
                color={DEFAULT_QR_CODE_COLOR}
                leftSection={<IconExternalLink size={16} />}
              >
                {t('partnerGuide.openPortfolio')}
              </Button>
              <Button
                component="a"
                href="mailto:service@robotop.de?subject=Partner%20embed%20allowlist%20request"
                variant="default"
                leftSection={<IconMail size={16} />}
              >
                {t('partnerGuide.contactAllowlist')}
              </Button>
            </Group>
          </Box>

          <div className={classes.mobileToc}>
            {TOC.map((item) => (
              <Badge key={item.id} component="a" href={`#${item.id}`} variant="light" color="gray">
                {t(`partnerGuide.${item.key}`)}
              </Badge>
            ))}
          </div>

          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Paper withBorder p="md" radius="md">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6}>
                {t('partnerGuide.endpointPortfolio')}
              </Text>
              <Anchor href={PRODUCTION_ORIGIN} target="_blank" rel="noreferrer" size="sm" fw={600}>
                {PRODUCTION_ORIGIN}
              </Anchor>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6}>
                {t('partnerGuide.endpointLogin')}
              </Text>
              <Code style={{ fontSize: 12, wordBreak: 'break-all' }}>{PRODUCTION_LOGIN}</Code>
            </Paper>
            <Paper withBorder p="md" radius="md">
              <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6}>
                {t('partnerGuide.endpointUnity')}
              </Text>
              <Code style={{ fontSize: 12, wordBreak: 'break-all' }}>{PRODUCTION_UNITY_IFRAME}</Code>
            </Paper>
          </SimpleGrid>

          <div id="overview">
            <Title order={2} mb="md" fz="1.35rem">
              {t('partnerGuide.whatYouGetTitle')}
            </Title>
            <Text c="dimmed" mb="lg" maw={800} lh={1.6}>
              {t('partnerGuide.whatYouGetBody')}
            </Text>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <PartnerPageFigure />
              <LoginPopupFigure />
            </SimpleGrid>
          </div>

          <SectionCard
            id="start"
            icon={<IconListCheck size={22} />}
            title={t('partnerGuide.beforeTitle')}
          >
            <Text size="sm" c="dimmed" mb="md" lh={1.6}>
              {t('partnerGuide.beforeBody')}
            </Text>
            <ol className={classes.stepList}>
              <li>
                <Text size="sm">{t('partnerGuide.beforeItem1')}</Text>
              </li>
              <li>
                <Text size="sm">{t('partnerGuide.beforeItem2')}</Text>
              </li>
              <li>
                <Text size="sm">{t('partnerGuide.beforeItem3')}</Text>
              </li>
              <li>
                <Text size="sm">{t('partnerGuide.beforeItem4')}</Text>
              </li>
            </ol>
            <Alert icon={<IconInfoCircle size={18} />} color="blue" radius="md" mt="md">
              <Text size="sm">{t('partnerGuide.beforeEditorsNote')}</Text>
            </Alert>
          </SectionCard>

          <div id="how">
            <Title order={2} mb="md" fz="1.35rem">
              {t('partnerGuide.howItWorksTitle')}
            </Title>
            <Text c="dimmed" mb="lg" maw={800} lh={1.6}>
              {t('partnerGuide.howItWorksBody')}
            </Text>
            <Grid gutter="sm">
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <FlowStep label={t('partnerGuide.flow1Title')} sub={t('partnerGuide.flow1Body')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <FlowStep label={t('partnerGuide.flow2Title')} sub={t('partnerGuide.flow2Body')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <FlowStep label={t('partnerGuide.flow3Title')} sub={t('partnerGuide.flow3Body')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <FlowStep label={t('partnerGuide.flow4Title')} sub={t('partnerGuide.flow4Body')} />
              </Grid.Col>
            </Grid>
            <Alert icon={<IconInfoCircle size={18} />} color="blue" radius="md" mt="lg">
              <Text size="sm">{t('partnerGuide.cookieNote')}</Text>
            </Alert>
          </div>

          <div id="steps">
            <Stack gap="lg">
              <SectionCard step={1} icon={<IconAppWindow size={22} />} title={t('partnerGuide.step1Title')}>
                <Text size="sm" c="dimmed" mb="md" lh={1.6}>
                  {t('partnerGuide.step1Body')}
                </Text>
                <Box mb="md" maw={520}>
                  <PartnerPageFigure />
                </Box>
                <div className={classes.callout}>
                  <Text size="sm" lh={1.55}>
                    {t('partnerGuide.step1Hint')}
                  </Text>
                </div>
                <Box mt="md">
                  <CodeBlock code={iframeSnippet} label="HTML" />
                </Box>
              </SectionCard>

              <SectionCard step={2} icon={<IconLogin2 size={22} />} title={t('partnerGuide.step2Title')}>
                <Text size="sm" c="dimmed" mb="md" lh={1.6}>
                  {t('partnerGuide.step2Body')}
                </Text>
                <Box mb="md" maw={420}>
                  <LoginPopupFigure />
                </Box>
                <CodeBlock code={popupSnippet} label="JavaScript" />
                <List size="sm" mt="md" spacing="sm">
                  <List.Item>
                    <Code>popup=1</Code> — {t('partnerGuide.step2PopupFlag')}
                  </List.Item>
                  <List.Item>
                    <Code>opener_origin</Code> — {t('partnerGuide.step2Opener')}
                  </List.Item>
                </List>
                <Alert icon={<IconInfoCircle size={18} />} color="gray" radius="md" mt="md">
                  <Text size="sm">{t('partnerGuide.step2Hint')}</Text>
                </Alert>
              </SectionCard>

              <SectionCard step={3} icon={<IconCode size={22} />} title={t('partnerGuide.step3Title')}>
                <Text size="sm" c="dimmed" mb="md" lh={1.6}>
                  {t('partnerGuide.step3Body')}
                </Text>
                <div className={classes.callout}>
                  <Text size="sm" lh={1.55} mb="sm">
                    {t('partnerGuide.step3Hint')}
                  </Text>
                </div>
                <Box mt="md">
                  <CodeBlock code={messageSnippet} label="JavaScript" />
                </Box>
                <Group gap="xs" mt="md">
                  <Badge variant="light" color="gray">
                    {ROBOTOP_POPUP_LOGIN_MSG}
                  </Badge>
                  <Badge variant="light" color="gray">
                    {ROBOTOP_UNITY_SESSION_MSG}
                  </Badge>
                  <Badge variant="light" color="gray">
                    robotop-unity-session-ready
                  </Badge>
                </Group>
              </SectionCard>

              <SectionCard
                step={4}
                icon={<IconShieldCheck size={22} />}
                title={t('partnerGuide.step4Title')}
              >
                <Text size="sm" c="dimmed" mb="sm" lh={1.6}>
                  {t('partnerGuide.step4Body')}
                </Text>
                <List size="sm" spacing="sm">
                  <List.Item>{t('partnerGuide.step4Item1')}</List.Item>
                  <List.Item>{t('partnerGuide.step4Item2')}</List.Item>
                  <List.Item>{t('partnerGuide.step4Item3')}</List.Item>
                </List>
                <Text size="sm" mt="md">
                  {t('partnerGuide.step4Contact')}{' '}
                  <Anchor href="mailto:service@robotop.de">service@robotop.de</Anchor>
                </Text>
              </SectionCard>
            </Stack>
          </div>

          <SectionCard
            id="snippet"
            icon={<IconCopy size={22} />}
            title={t('partnerGuide.fullSnippetTitle')}
          >
            <Text size="sm" c="dimmed" mb="md" lh={1.6}>
              {t('partnerGuide.fullSnippetBody')}
            </Text>
            <Button
              variant="light"
              color={DEFAULT_QR_CODE_COLOR}
              mb="md"
              onClick={() => setShowFullSnippet((v) => !v)}
            >
              {showFullSnippet ? t('partnerGuide.hideSnippet') : t('partnerGuide.showSnippet')}
            </Button>
            {showFullSnippet ? <CodeBlock code={fullSnippet} label="HTML + JavaScript" /> : null}
          </SectionCard>

          <SimpleGrid id="checklist" cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper withBorder p="xl" radius="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon color="teal" variant="light" size={36} radius="md">
                  <IconListCheck size={18} />
                </ThemeIcon>
                <Title order={3} fz="1.15rem">
                  {t('partnerGuide.checklistTitle')}
                </Title>
              </Group>
              <List size="sm" spacing="sm" icon={<IconCheck size={14} color="teal" />}>
                <List.Item>{t('partnerGuide.check1')}</List.Item>
                <List.Item>{t('partnerGuide.check2')}</List.Item>
                <List.Item>{t('partnerGuide.check3')}</List.Item>
                <List.Item>{t('partnerGuide.check4')}</List.Item>
                <List.Item>{t('partnerGuide.check5')}</List.Item>
                <List.Item>{t('partnerGuide.check6')}</List.Item>
              </List>
            </Paper>

            <Paper id="security" withBorder p="xl" radius="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon color="orange" variant="light" size={36} radius="md">
                  <IconAlertTriangle size={18} />
                </ThemeIcon>
                <Title order={3} fz="1.15rem">
                  {t('partnerGuide.notesTitle')}
                </Title>
              </Group>
              <List size="sm" spacing="sm">
                <List.Item>{t('partnerGuide.note1')}</List.Item>
                <List.Item>{t('partnerGuide.note2')}</List.Item>
                <List.Item>{t('partnerGuide.note3')}</List.Item>
                <List.Item>{t('partnerGuide.note4')}</List.Item>
              </List>
            </Paper>
          </SimpleGrid>

          <Paper id="faq" withBorder p="xl" radius="lg">
            <Group gap="sm" mb="lg">
              <ThemeIcon color={DEFAULT_QR_CODE_COLOR} variant="light" size={36} radius="md">
                <IconHelpCircle size={18} />
              </ThemeIcon>
              <Title order={3} fz="1.15rem">
                {t('partnerGuide.faqTitle')}
              </Title>
            </Group>
            <Stack gap="lg">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n}>
                  <div className={classes.faqQ}>{t(`partnerGuide.faq${n}Q`)}</div>
                  <div className={classes.faqA}>{t(`partnerGuide.faq${n}A`)}</div>
                </div>
              ))}
            </Stack>
          </Paper>

          <Divider />

          <Text size="sm" c="dimmed" ta="center">
            {t('partnerGuide.footer')}{' '}
            <Anchor href={PRODUCTION_ORIGIN} target="_blank" rel="noreferrer">
              portfolio.robotop.de
            </Anchor>
          </Text>
        </Stack>
      </div>
    </Box>
  );
}
